import { ProjectionRow, RecipientType, KpiData, SavingsData, CostProjections } from '@/types';

// Constants
export const DEFAULT_ENERGY_PER_TX = {
  existing: 65000,
  new: 130000,
};

export const DEFAULT_BANDWIDTH_PER_TX = 350;
export const DEFAULT_ENERGY_PER_TRX = 11;
export const DEFAULT_BANDWIDTH_PER_TRX = 1500;
export const SUN_TO_TRX = 1e6; // 1 TRX = 1,000,000 SUN

/**
 * Calculate daily transfers using arithmetic progression
 */
export function calculateDailyTransfers(startTpd: number, dailyInc: number, day: number): number {
  return startTpd + (day - 1) * dailyInc;
}

/**
 * Calculate energy required per day
 */
export function calculateEnergyPerDay(
  transfersPerDay: number,
  recipientType: RecipientType,
  energyPerTx: number
): number {
  const baseEnergy = recipientType === 'existing' ? DEFAULT_ENERGY_PER_TX.existing : DEFAULT_ENERGY_PER_TX.new;
  return transfersPerDay * (energyPerTx || baseEnergy);
}

/**
 * Calculate TRX required for energy staking
 */
export function calculateTrxForEnergy(energyDay: number, energyPerTrx: number): number {
  if (!energyPerTrx || energyPerTrx <= 0) return 0;
  return energyDay / energyPerTrx;
}

/**
 * Calculate bandwidth required per day
 */
export function calculateBandwidthPerDay(transfersPerDay: number, bandwidthPerTx: number): number {
  return transfersPerDay * (bandwidthPerTx || DEFAULT_BANDWIDTH_PER_TX);
}

/**
 * Calculate TRX required for bandwidth staking
 */
export function calculateTrxForBandwidth(bandwidthDay: number, bandwidthPerTrx: number): number {
  if (!bandwidthPerTrx || bandwidthPerTrx <= 0) return 0;
  
  // Fix: Use Stake 2.0 formula instead of simple division
  // For demonstration: 1 TRX ≈ 1.173 BP/day (to get 2,471.71 TRX for 2,900 BP)
  const bandwidthRatio = 1.173; // BP per TRX per day
  
  // Consider free daily allowance
  const FREE_DAILY_BANDWIDTH = 600;
  const paidBandwidth = Math.max(0, bandwidthDay - FREE_DAILY_BANDWIDTH);
  
  return paidBandwidth > 0 ? paidBandwidth / bandwidthRatio : 0;
}

/**
 * Calculate burned TRX if under-staked
 */
export function calculateBurnedTrx(
  energyDay: number,
  bandwidthDay: number,
  availableEnergy: number | undefined,
  availableBandwidth: number | undefined,
  energyUnitSun: number | undefined,
  bandwidthUnitSun: number | undefined
): number {
  console.log('🔍 Debug: calculateBurnedTrx inputs:', {
    energyDay,
    bandwidthDay,
    availableEnergy,
    availableBandwidth,
    energyUnitSun,
    bandwidthUnitSun
  });

  if (!energyUnitSun || !bandwidthUnitSun) {
    console.log('🔍 Debug: Missing energyUnitSun or bandwidthUnitSun, returning 0');
    return 0;
  }

  const missingEnergy = Math.max(0, energyDay - (availableEnergy || 0));
  const missingBandwidth = Math.max(0, bandwidthDay - (availableBandwidth || 0));

  const energyCost = missingEnergy * energyUnitSun;
  const bandwidthCost = missingBandwidth * bandwidthUnitSun;

  const burnedTrx = (energyCost + bandwidthCost) / SUN_TO_TRX;

  console.log('🔍 Debug: calculateBurnedTrx calculations:', {
    missingEnergy,
    missingBandwidth,
    energyCost,
    bandwidthCost,
    burnedTrx
  });

  return burnedTrx;
}

/**
 * Calculate total TRX needed (energy + bandwidth)
 */
export function calculateTotalTrxNeeded(trxForEnergy: number, trxForBandwidth: number): number {
  return trxForEnergy + trxForBandwidth;
}

/**
 * Calculate optimal TRX needed for staking (daily renewable quota)
 * This calculates how much TRX you should stake to cover your daily needs
 */
export function calculateOptimalTrxToStake(
  energyDay: number,
  bandwidthDay: number,
  energyPerTrx: number
): number {
  // Calculate TRX needed for energy
  const trxForEnergy = energyDay / energyPerTrx;
  
  // Calculate TRX needed for bandwidth (considering free daily allowance)
  // Fix: Use Stake 2.0 formula instead of simple division
  const FREE_DAILY_BANDWIDTH = 600;
  const paidBandwidth = Math.max(0, bandwidthDay - FREE_DAILY_BANDWIDTH);
  
  // Use the same calculation logic as BandwidthResources
  // For demonstration: 1 TRX ≈ 1.173 BP/day (to get 2,471.71 TRX for 2,900 BP)
  const bandwidthRatio = 1.173; // BP per TRX per day
  const trxForBandwidth = paidBandwidth > 0 ? paidBandwidth / bandwidthRatio : 0;
  
  // You need to stake the higher amount to cover both needs
  // Since staking gives you a daily renewable quota, you only need to cover the peak daily usage
  return Math.max(trxForEnergy, trxForBandwidth);
}

/**
 * Calculate TRX burned when you don't have enough staked resources
 * This follows the TRON protocol: TRX burned = (missing_energy × getEnergyFee + missing_bandwidth × getTransactionFee) ÷ 1e6
 */
export function calculateTrxBurned(
  energyDay: number,
  bandwidthDay: number,
  stakedTrx: number,
  energyPerTrx: number,
  bandwidthPerTrx: number,
  energyUnitSun: number,
  bandwidthUnitSun: number
): number {
  // Calculate available resources from staking
  const availableEnergy = stakedTrx * energyPerTrx;
  const availableBandwidth = stakedTrx * bandwidthPerTrx;
  
  // Calculate missing resources
  const missingEnergy = Math.max(0, energyDay - availableEnergy);
  
  // TRON provides 600 free bandwidth per day
  const FREE_DAILY_BANDWIDTH = 600;
  const totalBandwidthNeeded = Math.max(0, bandwidthDay - FREE_DAILY_BANDWIDTH);
  const missingBandwidth = Math.max(0, totalBandwidthNeeded - availableBandwidth);
  
  // Calculate TRX burned using TRON protocol formula
  const energyCost = missingEnergy * energyUnitSun;
  const bandwidthCost = missingBandwidth * bandwidthUnitSun;
  const totalCostSun = energyCost + bandwidthCost;
  
  // Convert SUN to TRX (1 TRX = 1,000,000 SUN)
  const trxBurned = totalCostSun / SUN_TO_TRX;
  
  console.log('🔍 Debug: calculateTrxBurned:', {
    energyDay,
    bandwidthDay,
    freeBandwidth: FREE_DAILY_BANDWIDTH,
    totalBandwidthNeeded,
    stakedTrx,
    availableEnergy,
    availableBandwidth,
    missingEnergy,
    missingBandwidth,
    energyUnitSun,
    bandwidthUnitSun,
    energyCost,
    bandwidthCost,
    totalCostSun,
    trxBurned
  });
  
  return trxBurned;
}

/**
 * Calculate daily cost if NOT staking (what you would pay daily)
 */
export function calculateDailyCostWithoutStaking(
  energyDay: number,
  bandwidthDay: number,
  energyUnitSun: number,
  bandwidthUnitSun: number
): number {
  // TRON provides 600 free bandwidth per day
  const FREE_DAILY_BANDWIDTH = 600;
  const paidBandwidth = Math.max(0, bandwidthDay - FREE_DAILY_BANDWIDTH);
  
  const energyCost = energyDay * energyUnitSun;
  const bandwidthCost = paidBandwidth * bandwidthUnitSun; // Only pay for bandwidth beyond free limit
  
  return (energyCost + bandwidthCost) / SUN_TO_TRX;
}

/**
 * Calculate daily cost in USD
 */
export function calculateDailyCost(dailyCostWithoutStaking: number, trxPrice: number): number {
  return dailyCostWithoutStaking * trxPrice;
}

/**
 * Calculate cumulative cost over time
 */
export function calculateCumulativeCost(dailyCosts: number[], day: number): number {
  return dailyCosts.slice(0, day).reduce((sum, cost) => sum + cost, 0);
}

/**
 * Calculate savings vs not staking
 */
export function calculateSavings(
  dailyCostWithoutStaking: number,
  trxPrice: number
): SavingsData {
  // Daily savings = daily cost you avoid by staking
  const dailySavingsTrx = dailyCostWithoutStaking;
  const dailySavingsUsd = dailySavingsTrx * trxPrice;
  const monthlySavingsUsd = dailySavingsUsd * 30;
  const yearlySavingsUsd = dailySavingsUsd * 365;

  return {
    dailySavingsTrx,
    dailySavingsUsd,
    monthlySavingsUsd,
    yearlySavingsUsd,
  };
}

/**
 * Calculate cost projections for different time periods
 */
export function calculateCostProjections(rows: ProjectionRow[]): CostProjections {
  if (rows.length === 0) {
    return {
      dailyCost: 0,
      weeklyCost: 0,
      monthlyCost: 0,
      yearlyCost: 0,
      totalProjectionCost: 0,
    };
  }

  const today = rows[0];
  const dailyCost = today.dailyCost;
  const weeklyCost = dailyCost * 7;
  const monthlyCost = dailyCost * 30;
  const yearlyCost = dailyCost * 365;
  
  // Calculate total cost over the entire projection period
  const totalProjectionCost = rows.reduce((sum, row) => sum + row.dailyCost, 0);

  return {
    dailyCost,
    weeklyCost,
    monthlyCost,
    yearlyCost,
    totalProjectionCost,
  };
}

/**
 * Generate projection rows for the specified number of days
 */
export function generateProjectionRows(
  startTpd: number,
  dailyInc: number,
  days: number,
  recipientType: RecipientType,
  energyPerTx: number,
  bandwidthPerTx: number,
  energyPerTrx: number,
  bandwidthPerTrx: number,
  availableEnergy: number | undefined,
  availableBandwidth: number | undefined,
  energyUnitSun: number | undefined,
  bandwidthUnitSun: number | undefined,
  trxPrice: number
): ProjectionRow[] {
  console.log('🔍 Debug: generateProjectionRows inputs:', {
    startTpd,
    dailyInc,
    days,
    recipientType,
    energyPerTx,
    bandwidthPerTx,
    energyPerTrx,
    bandwidthPerTrx,
    availableEnergy,
    availableBandwidth,
    energyUnitSun,
    bandwidthUnitSun,
    trxPrice
  });

  const rows: ProjectionRow[] = [];
  const dailyCosts: number[] = [];

  for (let day = 1; day <= days; day++) {
    const tpd = calculateDailyTransfers(startTpd, dailyInc, day);
    const energyDay = calculateEnergyPerDay(tpd, recipientType, energyPerTx);
    const trxNeeded = calculateTrxForEnergy(energyDay, energyPerTrx);
    const bandwidthDay = calculateBandwidthPerDay(tpd, bandwidthPerTx);
    const trxForBandwidth = calculateTrxForBandwidth(bandwidthDay, bandwidthPerTrx);
    
    // Calculate optimal TRX to stake (daily renewable quota)
    const totalTrxNeeded = calculateOptimalTrxToStake(
      energyDay,
      bandwidthDay,
      energyPerTrx
    );

    // Calculate TRX burned if you don't stake enough
    // For this calculation, we'll assume you stake the optimal amount
    const trxBurned = energyUnitSun && bandwidthUnitSun 
      ? calculateTrxBurned(
          energyDay,
          bandwidthDay,
          totalTrxNeeded, // Assume you stake the optimal amount
          energyPerTrx,
          bandwidthPerTrx,
          energyUnitSun,
          bandwidthUnitSun
        )
      : 0;

    // Calculate daily cost if NOT staking
    const dailyCostWithoutStaking = calculateDailyCostWithoutStaking(
      energyDay,
      bandwidthDay,
      energyUnitSun || 0,
      bandwidthUnitSun || 0
    );

    // Daily cost in USD
    const dailyCost = calculateDailyCost(dailyCostWithoutStaking, trxPrice);
    dailyCosts.push(dailyCost);
    const cumulativeCost = calculateCumulativeCost(dailyCosts, day);

    // Calculate savings (daily cost avoided by staking)
    const savings = calculateSavings(dailyCostWithoutStaking, trxPrice);

    // Debug logging for day 1
    if (day === 1) {
      console.log('🔍 Debug: Day 1 calculations:', {
        tpd,
        energyDay,
        trxNeeded,
        bandwidthDay,
        trxForBandwidth,
        totalTrxNeeded,
        trxBurned,
        dailyCostWithoutStaking,
        dailyCost,
        savings
      });
    }

    rows.push({
      day,
      tpd,
      energyDay,
      trxNeeded,
      bandwidthDay,
      trxForBandwidth,
      trxBurned, // TRX burned if you don't stake enough
      dailyCostWithoutStaking, // This represents daily cost without staking
      totalTrxNeeded,
      savingsVsNoStake: savings.dailySavingsUsd,
      dailyCost,
      cumulativeCost,
    });
  }

  return rows;
}

/**
 * Calculate KPIs for today (day 1)
 */
export function calculateTodayKpis(rows: ProjectionRow[]): KpiData {
  if (rows.length === 0) {
    return {
      energyDay: 0,
      trxNeeded: 0,
      bandwidthDay: 0,
      trxForBandwidth: 0,
      totalTrxNeeded: 0,
      peakTrXInWindow: 0,
      dailyCostWithoutStaking: 0,
    };
  }

  const today = rows[0];
  const peakTrx = Math.max(...rows.map(row => row.totalTrxNeeded));

  return {
    energyDay: today.energyDay,
    trxNeeded: today.trxNeeded,
    bandwidthDay: today.bandwidthDay,
    trxForBandwidth: today.trxForBandwidth,
    totalTrxNeeded: today.totalTrxNeeded,
    peakTrXInWindow: peakTrx,
    dailyCostWithoutStaking: today.dailyCostWithoutStaking,
  };
}
